import type maplibregl from 'maplibre-gl';
import { EventEmitter } from '../events/MapEvent';
import type { Source } from '../sources/Source';
import type { Extent } from '../geometry/Extent';
import {
  Style,
  styleToCircleProps,
  styleToLineProps,
  styleToFillProps,
  styleToTextProps,
} from '../style/Style';

let layerIdCounter = 0;

export interface LayerOptions {
  id?: string;
  visible?: boolean;
  opacity?: number;
  zIndex?: number;
  minZoom?: number;
  maxZoom?: number;
  extent?: Extent;
  source?: Source;
}

export class Layer extends EventEmitter {
  public id: string;
  public visible: boolean;
  public opacity: number;
  public zIndex: number;
  public minZoom: number;
  public maxZoom: number;
  public extent?: Extent;
  protected _source?: Source;

  protected _appliedMap?: maplibregl.Map;
  protected _managedSourceIds: string[] = [];
  protected _managedLayerIds: string[] = [];

  constructor(options: LayerOptions = {}) {
    super();
    this.id = options.id || `layer-${++layerIdCounter}`;
    this.visible = options.visible !== false;
    this.opacity = options.opacity ?? 1;
    this.zIndex = options.zIndex ?? 0;
    this.minZoom = options.minZoom ?? 0;
    this.maxZoom = options.maxZoom ?? 22;
    this.extent = options.extent;
    this._source = options.source;
  }

  setVisible(visible: boolean): void {
    this.visible = visible;
    if (this._appliedMap) {
      for (const lid of this._managedLayerIds) {
        if (this._appliedMap.getLayer(lid)) {
          this._appliedMap.setLayoutProperty(lid, 'visibility', visible ? 'visible' : 'none');
        }
      }
    }
    this.emit('visibilitychange', { visible });
  }

  isVisible(): boolean {
    return this.visible;
  }

  setOpacity(opacity: number): void {
    this.opacity = Math.max(0, Math.min(1, opacity));
    if (this._appliedMap) this._applyOpacityToManagedLayers();
    this.emit('opacitychange', { opacity: this.opacity });
  }

  protected _applyOpacityToManagedLayers(): void {
    if (!this._appliedMap) return;
    const map = this._appliedMap;
    for (const lid of this._managedLayerIds) {
      const lyr = map.getLayer(lid);
      if (!lyr) continue;
      const t = (lyr as any).type as string;
      try {
        if (t === 'raster') map.setPaintProperty(lid, 'raster-opacity', this.opacity);
        else if (t === 'circle') {
          map.setPaintProperty(lid, 'circle-opacity', this.opacity);
          map.setPaintProperty(lid, 'circle-stroke-opacity', this.opacity);
        }
        else if (t === 'line') map.setPaintProperty(lid, 'line-opacity', this.opacity);
        else if (t === 'fill') map.setPaintProperty(lid, 'fill-opacity', this.opacity * 0.5);
        else if (t === 'symbol') {
          map.setPaintProperty(lid, 'text-opacity', this.opacity);
          map.setPaintProperty(lid, 'icon-opacity', this.opacity);
        }
        else if (t === 'heatmap') map.setPaintProperty(lid, 'heatmap-opacity', this.opacity);
      } catch {}
    }
  }

  getOpacity(): number {
    return this.opacity;
  }

  setZIndex(index: number): void {
    this.zIndex = index;
  }

  getSource(): Source | undefined {
    return this._source;
  }

  setSource(source: Source): void {
    this._source = source;
    this.emit('sourcechange', { source });
  }

  refresh(): void {
    this._source?.refresh();
  }

  getExtent(): Extent | undefined {
    return this.extent;
  }

  /**
   * Render this layer into the given MapLibre map. Subclasses override.
   * The base implementation is a no-op for layer types with no 2D rendering.
   */
  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    this._appliedMap = map;
    void beforeId;
  }

  _removeFrom(mapHandle: unknown): void {
    const map = mapHandle as maplibregl.Map;
    for (const lid of this._managedLayerIds) {
      try { if (map.getLayer(lid)) map.removeLayer(lid); } catch {}
    }
    for (const sid of this._managedSourceIds) {
      try {
        if (this._source) this._source._detach(map, sid);
        if (map.getSource(sid)) map.removeSource(sid);
      } catch {}
    }
    this._managedLayerIds = [];
    this._managedSourceIds = [];
    this._appliedMap = undefined;
  }

  protected _ensureSource(map: maplibregl.Map, spec?: maplibregl.SourceSpecification): string | undefined {
    if (!this._source) return undefined;
    const sourceId = `${this.id}__src`;
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, (spec || this._source.toMaplibreSpec()) as maplibregl.SourceSpecification);
    }
    if (!this._managedSourceIds.includes(sourceId)) {
      this._managedSourceIds.push(sourceId);
      this._source._attach(map, sourceId);
    }
    return sourceId;
  }

  dispose(): void {
    if (this._appliedMap) this._removeFrom(this._appliedMap);
    this.removeAllListeners();
  }
}

export interface TileLayerOptions extends LayerOptions {
  source: Source;
  preload?: number;
  useInterimTilesOnError?: boolean;
  cacheSize?: number;
  transition?: number;
}

export class TileLayer extends Layer {
  public preload: number;
  public transition: number;

  constructor(options: TileLayerOptions) {
    super(options);
    this.preload = options.preload || 0;
    this.transition = options.transition ?? 250;
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = this._ensureSource(map);
    if (!sourceId) return;
    const layerId = `${this.id}__lyr`;
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        minzoom: this.minZoom,
        maxzoom: this.maxZoom,
        paint: { 'raster-opacity': this.opacity },
        layout: { visibility: this.visible ? 'visible' : 'none' },
      } as any, beforeId);
      this._managedLayerIds.push(layerId);
    }
  }
}

export interface VectorLayerOptions extends LayerOptions {
  source: Source;
  style?: Style | any;
  declutter?: boolean;
  updateWhileAnimating?: boolean;
  updateWhileInteracting?: boolean;
  renderBuffer?: number;
}

export class VectorLayer extends Layer {
  public style?: Style | any;
  public declutter: boolean;

  constructor(options: VectorLayerOptions) {
    super(options);
    this.style = options.style;
    this.declutter = options.declutter || false;
  }

  getFeatures(): any[] {
    const src: any = this._source;
    const data = src?.data;
    if (!data) return [];
    if (data.type === 'FeatureCollection') return data.features || [];
    if (data.type === 'Feature') return [data];
    return [];
  }

  setStyle(style: Style | any): void {
    this.style = style;
    if (this._appliedMap) this._reapplyStyle();
    this.emit('change');
  }

  getStyle(): any {
    return this.style;
  }

  protected _reapplyStyle(): void {
    if (!this._appliedMap) return;
    const map = this._appliedMap;
    const style = this.style instanceof Style ? this.style : undefined;

    const apply = (lid: string, props: Record<string, any>) => {
      if (!map.getLayer(lid)) return;
      for (const [k, v] of Object.entries(props)) {
        try { map.setPaintProperty(lid, k, v); } catch {}
      }
    };

    apply(`${this.id}__circle`, styleToCircleProps(style, this.opacity).paint);
    apply(`${this.id}__line`, styleToLineProps(style, this.opacity).paint);
    apply(`${this.id}__fill`, styleToFillProps(style, this.opacity).paint);
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = this._ensureSource(map);
    if (!sourceId) return;

    const style = this.style instanceof Style ? this.style : undefined;
    const visLayout = { visibility: (this.visible ? 'visible' : 'none') as 'visible' | 'none' };

    // Polygon fill
    const fillId = `${this.id}__fill`;
    if (!map.getLayer(fillId)) {
      const fp = styleToFillProps(style, this.opacity);
      map.addLayer({
        id: fillId,
        type: 'fill',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Polygon'],
        paint: fp.paint,
        layout: { ...fp.layout, ...visLayout },
        minzoom: this.minZoom,
        maxzoom: this.maxZoom,
      } as any, beforeId);
      this._managedLayerIds.push(fillId);
    }

    // Lines (LineString + Polygon outline)
    const lineId = `${this.id}__line`;
    if (!map.getLayer(lineId)) {
      const lp = styleToLineProps(style, this.opacity);
      map.addLayer({
        id: lineId,
        type: 'line',
        source: sourceId,
        filter: ['any',
          ['==', ['geometry-type'], 'LineString'],
          ['==', ['geometry-type'], 'Polygon'],
        ],
        paint: lp.paint,
        layout: { ...lp.layout, ...visLayout },
        minzoom: this.minZoom,
        maxzoom: this.maxZoom,
      } as any, beforeId);
      this._managedLayerIds.push(lineId);
    }

    // Points
    const circleId = `${this.id}__circle`;
    if (!map.getLayer(circleId)) {
      const cp = styleToCircleProps(style, this.opacity);
      map.addLayer({
        id: circleId,
        type: 'circle',
        source: sourceId,
        filter: ['==', ['geometry-type'], 'Point'],
        paint: cp.paint,
        layout: { ...cp.layout, ...visLayout },
        minzoom: this.minZoom,
        maxzoom: this.maxZoom,
      } as any, beforeId);
      this._managedLayerIds.push(circleId);
    }

    // Text labels
    if (style?.text) {
      const textId = `${this.id}__text`;
      if (!map.getLayer(textId)) {
        const tp = styleToTextProps(style, this.opacity);
        map.addLayer({
          id: textId,
          type: 'symbol',
          source: sourceId,
          paint: tp.paint,
          layout: { ...tp.layout, ...visLayout },
          minzoom: this.minZoom,
          maxzoom: this.maxZoom,
        } as any, beforeId);
        this._managedLayerIds.push(textId);
      }
    }
  }
}

export interface VectorTileLayerOptions extends LayerOptions {
  source: Source;
  sourceLayer?: string;
  style?: Style | any;
  declutter?: boolean;
  renderMode?: 'hybrid' | 'vector';
  preload?: number;
  geometryType?: 'fill' | 'line' | 'circle';
}

export class VectorTileLayer extends Layer {
  public style?: Style | any;
  public renderMode: string;
  public sourceLayer?: string;
  public geometryType: 'fill' | 'line' | 'circle';

  constructor(options: VectorTileLayerOptions) {
    super(options);
    this.style = options.style;
    this.renderMode = options.renderMode || 'hybrid';
    this.sourceLayer = options.sourceLayer;
    this.geometryType = options.geometryType || 'fill';
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = this._ensureSource(map);
    if (!sourceId) return;
    const srcAny = this._source as any;
    const sourceLayer = this.sourceLayer || srcAny?.layers?.[0];
    if (!sourceLayer) return;

    const style = this.style instanceof Style ? this.style : undefined;
    const layerId = `${this.id}__lyr`;
    const visLayout = { visibility: (this.visible ? 'visible' : 'none') as 'visible' | 'none' };

    if (!map.getLayer(layerId)) {
      let spec: any;
      if (this.geometryType === 'fill') {
        const fp = styleToFillProps(style, this.opacity);
        spec = { type: 'fill', paint: fp.paint, layout: { ...fp.layout, ...visLayout } };
      } else if (this.geometryType === 'line') {
        const lp = styleToLineProps(style, this.opacity);
        spec = { type: 'line', paint: lp.paint, layout: { ...lp.layout, ...visLayout } };
      } else {
        const cp = styleToCircleProps(style, this.opacity);
        spec = { type: 'circle', paint: cp.paint, layout: { ...cp.layout, ...visLayout } };
      }
      map.addLayer({
        id: layerId,
        source: sourceId,
        'source-layer': sourceLayer,
        minzoom: this.minZoom,
        maxzoom: this.maxZoom,
        ...spec,
      } as any, beforeId);
      this._managedLayerIds.push(layerId);
    }
  }
}

export interface HeatmapLayerOptions extends LayerOptions {
  source: Source;
  gradient?: string[];
  radius?: number;
  blur?: number;
  weight?: string | ((feature: any) => number);
  intensity?: number;
}

export class HeatmapLayer extends Layer {
  public gradient: string[];
  public radius: number;
  public blur: number;
  public intensity: number;
  public weightProp?: string;

  constructor(options: HeatmapLayerOptions) {
    super(options);
    this.gradient = options.gradient || ['blue', 'cyan', 'lime', 'yellow', 'red'];
    this.radius = options.radius || 8;
    this.blur = options.blur || 15;
    this.intensity = options.intensity ?? 1;
    this.weightProp = typeof options.weight === 'string' ? options.weight : undefined;
  }

  protected _gradientToColorRamp(): any {
    const stops: any[] = ['interpolate', ['linear'], ['heatmap-density']];
    const n = this.gradient.length;
    stops.push(0, 'rgba(0,0,0,0)');
    for (let i = 0; i < n; i++) {
      const t = (i + 1) / n;
      stops.push(t, this.gradient[i]);
    }
    return stops;
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = this._ensureSource(map);
    if (!sourceId) return;
    const layerId = `${this.id}__heat`;
    if (!map.getLayer(layerId)) {
      const paint: Record<string, any> = {
        'heatmap-radius': this.radius,
        'heatmap-color': this._gradientToColorRamp(),
        'heatmap-intensity': this.intensity,
        'heatmap-opacity': this.opacity,
      };
      if (this.weightProp) {
        paint['heatmap-weight'] = ['get', this.weightProp];
      }
      map.addLayer({
        id: layerId,
        type: 'heatmap',
        source: sourceId,
        minzoom: this.minZoom,
        maxzoom: this.maxZoom,
        paint,
        layout: { visibility: this.visible ? 'visible' : 'none' },
      } as any, beforeId);
      this._managedLayerIds.push(layerId);
    }
  }
}

export interface ClusterLayerOptions extends LayerOptions {
  source: Source;
  distance?: number;
  minDistance?: number;
  style?: any;
  clusterStyle?: any;
  clusterColors?: [string, string, string];
  clusterRadii?: [number, number, number];
  pointColor?: string;
  pointRadius?: number;
}

export class ClusterLayer extends Layer {
  public distance: number;
  public minDistance: number;
  public clusterStyle?: any;
  public clusterColors: [string, string, string];
  public clusterRadii: [number, number, number];
  public pointColor: string;
  public pointRadius: number;

  constructor(options: ClusterLayerOptions) {
    super(options);
    this.distance = options.distance || 40;
    this.minDistance = options.minDistance || 20;
    this.clusterStyle = options.clusterStyle;
    this.clusterColors = options.clusterColors || ['#51bbd6', '#f1f075', '#f28cb1'];
    this.clusterRadii = options.clusterRadii || [15, 20, 25];
    this.pointColor = options.pointColor || '#11b4da';
    this.pointRadius = options.pointRadius || 5;
  }

  setDistance(distance: number): void {
    this.distance = distance;
  }

  getDistance(): number {
    return this.distance;
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    if (!this._source) return;
    const sourceId = `${this.id}__src`;
    if (!map.getSource(sourceId)) {
      const baseSpec: any = this._source.toMaplibreSpec();
      baseSpec.cluster = true;
      baseSpec.clusterRadius = this.distance;
      map.addSource(sourceId, baseSpec);
    }
    if (!this._managedSourceIds.includes(sourceId)) {
      this._managedSourceIds.push(sourceId);
      this._source._attach(map, sourceId);
    }

    const clusterId = `${this.id}__clusters`;
    const countId = `${this.id}__count`;
    const pointId = `${this.id}__point`;
    const visLayout = { visibility: (this.visible ? 'visible' : 'none') as 'visible' | 'none' };

    if (!map.getLayer(clusterId)) {
      map.addLayer({
        id: clusterId,
        type: 'circle',
        source: sourceId,
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': [
            'step', ['get', 'point_count'],
            this.clusterColors[0], 10,
            this.clusterColors[1], 50,
            this.clusterColors[2],
          ],
          'circle-radius': [
            'step', ['get', 'point_count'],
            this.clusterRadii[0], 10,
            this.clusterRadii[1], 50,
            this.clusterRadii[2],
          ],
          'circle-opacity': this.opacity,
        },
        layout: visLayout,
      } as any, beforeId);
      this._managedLayerIds.push(clusterId);
    }

    if (!map.getLayer(countId)) {
      map.addLayer({
        id: countId,
        type: 'symbol',
        source: sourceId,
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-size': 12,
          ...visLayout,
        },
        paint: { 'text-color': '#1f2937' },
      } as any, beforeId);
      this._managedLayerIds.push(countId);
    }

    if (!map.getLayer(pointId)) {
      map.addLayer({
        id: pointId,
        type: 'circle',
        source: sourceId,
        filter: ['!', ['has', 'point_count']],
        paint: {
          'circle-color': this.pointColor,
          'circle-radius': this.pointRadius,
          'circle-stroke-width': 1,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': this.opacity,
        },
        layout: visLayout,
      } as any, beforeId);
      this._managedLayerIds.push(pointId);
    }
  }
}

export interface ImageLayerOptions extends LayerOptions {
  url?: string;
  coordinates?: [[number, number], [number, number], [number, number], [number, number]];
}

export class ImageLayer extends Layer {
  public url?: string;
  public coordinates?: [[number, number], [number, number], [number, number], [number, number]];

  constructor(options: ImageLayerOptions) {
    super(options);
    this.url = options.url;
    this.coordinates = options.coordinates;
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    if (!this.url || !this.coordinates) return;
    const sourceId = `${this.id}__src`;
    const layerId = `${this.id}__lyr`;
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'image', url: this.url, coordinates: this.coordinates } as any);
      this._managedSourceIds.push(sourceId);
    }
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'raster',
        source: sourceId,
        paint: { 'raster-opacity': this.opacity },
        layout: { visibility: this.visible ? 'visible' : 'none' },
      } as any, beforeId);
      this._managedLayerIds.push(layerId);
    }
  }
}

export interface TerrainLayerOptions extends LayerOptions {
  source: Source;
  exaggeration?: number;
  shading?: 'hillshade' | 'slope' | 'aspect' | 'none';
}

export class TerrainLayer extends Layer {
  public exaggeration: number;
  public shading: 'hillshade' | 'slope' | 'aspect' | 'none';

  constructor(options: TerrainLayerOptions) {
    super(options);
    this.exaggeration = options.exaggeration || 1.0;
    this.shading = options.shading || 'hillshade';
  }

  setExaggeration(factor: number): void {
    this.exaggeration = factor;
    if (this._appliedMap) {
      try { this._appliedMap.setTerrain({ source: `${this.id}__src`, exaggeration: factor } as any); } catch {}
    }
  }

  getExaggeration(): number {
    return this.exaggeration;
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = this._ensureSource(map);
    if (!sourceId) return;
    try {
      map.setTerrain({ source: sourceId, exaggeration: this.exaggeration } as any);
    } catch {}
    if (this.shading === 'hillshade') {
      const layerId = `${this.id}__hillshade`;
      if (!map.getLayer(layerId)) {
        map.addLayer({
          id: layerId,
          type: 'hillshade',
          source: sourceId,
          paint: { 'hillshade-exaggeration': this.exaggeration * 0.5 },
          layout: { visibility: this.visible ? 'visible' : 'none' },
        } as any, beforeId);
        this._managedLayerIds.push(layerId);
      }
    }
  }

  _removeFrom(mapHandle: unknown): void {
    const map = mapHandle as maplibregl.Map;
    try { map.setTerrain(null as any); } catch {}
    super._removeFrom(map);
  }
}

export class GlobeLayer extends Layer {
  constructor(options: LayerOptions) {
    super(options);
  }
}

export class GroupLayer extends Layer {
  public layers: Layer[];

  constructor(options: LayerOptions & { layers?: Layer[] }) {
    super(options);
    this.layers = options.layers || [];
  }

  addLayer(layer: Layer): void {
    this.layers.push(layer);
    if (this._appliedMap) layer._applyTo(this._appliedMap);
  }

  removeLayer(layer: Layer): void {
    const idx = this.layers.indexOf(layer);
    if (idx >= 0) {
      this.layers.splice(idx, 1);
      if (this._appliedMap) layer._removeFrom(this._appliedMap);
    }
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    for (const child of this.layers) child._applyTo(map, beforeId);
  }

  _removeFrom(mapHandle: unknown): void {
    const map = mapHandle as maplibregl.Map;
    for (const child of this.layers) child._removeFrom(map);
    super._removeFrom(map);
  }

  setVisible(visible: boolean): void {
    super.setVisible(visible);
    for (const child of this.layers) child.setVisible(visible);
  }
}

export class GraphicsLayer extends Layer {
  private _features: GeoJSON.Feature[] = [];
  public style?: Style;

  constructor(options: LayerOptions & { style?: Style }) {
    super(options);
    this.style = options.style;
  }

  addFeature(feature: GeoJSON.Feature): void {
    this._features.push(feature);
    this._pushToMap();
  }

  setFeatures(features: GeoJSON.Feature[]): void {
    this._features = features.slice();
    this._pushToMap();
  }

  clear(): void {
    this._features = [];
    this._pushToMap();
  }

  getFeatures(): GeoJSON.Feature[] {
    return this._features.slice();
  }

  private _pushToMap(): void {
    const map = this._appliedMap;
    if (!map) return;
    const sourceId = `${this.id}__src`;
    const fc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: this._features };
    const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    if (src && typeof src.setData === 'function') src.setData(fc as any);
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = `${this.id}__src`;
    const fc: GeoJSON.FeatureCollection = { type: 'FeatureCollection', features: this._features };
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: fc as any } as any);
      this._managedSourceIds.push(sourceId);
    }
    const visLayout = { visibility: (this.visible ? 'visible' : 'none') as 'visible' | 'none' };
    const fillId = `${this.id}__fill`;
    const lineId = `${this.id}__line`;
    const circleId = `${this.id}__circle`;
    if (!map.getLayer(fillId)) {
      const fp = styleToFillProps(this.style, this.opacity);
      map.addLayer({ id: fillId, type: 'fill', source: sourceId, filter: ['==', ['geometry-type'], 'Polygon'], paint: fp.paint, layout: { ...fp.layout, ...visLayout } } as any, beforeId);
      this._managedLayerIds.push(fillId);
    }
    if (!map.getLayer(lineId)) {
      const lp = styleToLineProps(this.style, this.opacity);
      map.addLayer({ id: lineId, type: 'line', source: sourceId, filter: ['any', ['==', ['geometry-type'], 'LineString'], ['==', ['geometry-type'], 'Polygon']], paint: lp.paint, layout: { ...lp.layout, ...visLayout } } as any, beforeId);
      this._managedLayerIds.push(lineId);
    }
    if (!map.getLayer(circleId)) {
      const cp = styleToCircleProps(this.style, this.opacity);
      map.addLayer({ id: circleId, type: 'circle', source: sourceId, filter: ['==', ['geometry-type'], 'Point'], paint: cp.paint, layout: { ...cp.layout, ...visLayout } } as any, beforeId);
      this._managedLayerIds.push(circleId);
    }
  }
}

export class AnnotationLayer extends GraphicsLayer {
  public editable: boolean;
  constructor(options: LayerOptions & { editable?: boolean; style?: Style }) {
    super(options);
    this.editable = options.editable || false;
  }
}

export class GridLayer extends Layer {
  public gridType: string;

  constructor(options: LayerOptions & { gridType?: string }) {
    super(options);
    this.gridType = options.gridType || 'latlng';
  }
}

export class MilitarySymbolLayer extends Layer {
  public standard: string;
  public symbolSize: number;
  public sidcField: string;
  private _features: GeoJSON.Feature[] = [];

  constructor(options: LayerOptions & { standard?: string; size?: number; sidcField?: string }) {
    super(options);
    this.standard = options.standard || '2525d';
    this.symbolSize = options.size || 35;
    this.sidcField = options.sidcField || 'sidc';
  }

  setFeatures(features: GeoJSON.Feature[]): void {
    this._features = features.slice();
    if (this._appliedMap) this._pushToMap();
  }

  private _pushToMap(): void {
    const map = this._appliedMap;
    if (!map) return;
    const sourceId = `${this.id}__src`;
    const src = map.getSource(sourceId) as maplibregl.GeoJSONSource | undefined;
    if (src && typeof src.setData === 'function') {
      src.setData({ type: 'FeatureCollection', features: this._features } as any);
    }
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = `${this.id}__src`;
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: { type: 'FeatureCollection', features: this._features } as any } as any);
      this._managedSourceIds.push(sourceId);
    }
    const layerId = `${this.id}__sym`;
    if (!map.getLayer(layerId)) {
      map.addLayer({
        id: layerId,
        type: 'circle',
        source: sourceId,
        paint: {
          'circle-color': '#dc2626',
          'circle-radius': this.symbolSize / 4,
          'circle-stroke-width': 2,
          'circle-stroke-color': '#ffffff',
          'circle-opacity': this.opacity,
        },
        layout: { visibility: this.visible ? 'visible' : 'none' },
      } as any, beforeId);
      this._managedLayerIds.push(layerId);
    }
  }
}

/**
 * Extruded fill layer (MapLibre fill-extrusion). Renders 3D-style buildings or
 * any polygon source as extruded prisms in the 2D map. Use this in place of
 * raw `map.addLayer({ type: 'fill-extrusion', ... })`.
 */
export interface ExtrusionLayerOptions extends LayerOptions {
  source: Source;
  sourceLayer?: string;
  /** Color expression, hex string, or interpolation expression. */
  color?: any;
  /** Height field name, fixed number, or expression. Defaults to ['get','height']. */
  height?: any;
  /** Base height field name, fixed number, or expression. Defaults to ['get','min_height']. */
  base?: any;
  /** Minimum zoom level at which extrusions appear. */
  minzoom?: number;
}

export class ExtrusionLayer extends Layer {
  public sourceLayer?: string;
  public color: any;
  public heightExpr: any;
  public baseExpr: any;
  public minzoom?: number;

  constructor(options: ExtrusionLayerOptions) {
    super(options);
    this.sourceLayer = options.sourceLayer;
    this.color = options.color ?? '#94a3b8';
    this.heightExpr = options.height ?? ['coalesce', ['get', 'render_height'], ['get', 'height'], 10];
    this.baseExpr = options.base ?? ['coalesce', ['get', 'render_min_height'], ['get', 'min_height'], 0];
    this.minzoom = options.minzoom;
  }

  setColor(color: any): void {
    this.color = color;
    if (this._appliedMap) {
      const lid = `${this.id}__ext`;
      if (this._appliedMap.getLayer(lid)) {
        try { this._appliedMap.setPaintProperty(lid, 'fill-extrusion-color', color); } catch {}
      }
    }
  }

  _applyTo(mapHandle: unknown, beforeId?: string): void {
    const map = mapHandle as maplibregl.Map;
    super._applyTo(map, beforeId);
    const sourceId = `${this.id}__src`;
    const src = this._source as any;
    if (src && !map.getSource(sourceId) && typeof src.toMaplibreSpec === 'function') {
      try {
        map.addSource(sourceId, src.toMaplibreSpec() as any);
        this._managedSourceIds.push(sourceId);
        if (typeof src._attach === 'function') src._attach(map, sourceId);
      } catch {}
    }
    const lid = `${this.id}__ext`;
    if (!map.getLayer(lid)) {
      const def: any = {
        id: lid,
        type: 'fill-extrusion',
        source: sourceId,
        paint: {
          'fill-extrusion-color': this.color,
          'fill-extrusion-height': this.heightExpr,
          'fill-extrusion-base': this.baseExpr,
          'fill-extrusion-opacity': this.opacity,
        },
        layout: { visibility: this.visible ? 'visible' : 'none' },
      };
      if (this.sourceLayer) def['source-layer'] = this.sourceLayer;
      if (this.minzoom !== undefined) def.minzoom = this.minzoom;
      try { map.addLayer(def, beforeId); this._managedLayerIds.push(lid); } catch {}
    }
  }
}
